/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import SwipeableViews from "react-swipeable-views";

import {
  Typography,
  Tabs,
  Tab,
  Tooltip,
  Button,
  LinearProgress,
} from "@material-ui/core";

// @material-ui/icons
import List from "@material-ui/icons/FormatListBulleted";
import {
  SkipNext,
  SkipPrevious,
  ArrowBack,
  Favorite,
  FavoriteBorder,
  FileCopy,
  Subtitles,
  PlaylistAddCheck,
  Link,
  Assignment,
} from "@material-ui/icons";

import { getCurrentInfoBox, ApricotAction } from "client/actions/infobox.ts";
import {
  selectorCurrentCourse,
  selectorCurrentLecture,
} from "client/selectors/courseSelector";
import {
  idTabClip,
  idTabNote,
  idTabProg,
  idShowClip,
  idLecPrev,
  idLecNext,
  idLecBack,
  idIBox,
} from "assets/jss/views/ElementIDs";

import sidePanelStyle from "assets/jss/views/classroomPage/sidePanelStyle";
import { infoboxType } from "client/reducers/infoboxReducer";
import { isCtMan } from "client/reducers/userReducer";
const styles = createStyles(sidePanelStyle as any);

interface Props extends WithStyles<typeof styles> {
  getCurrentInfoBox: ApricotAction;
  // store data
  currentCourse: any;
  currentLecture: any;
  user: any;
  // component props:
  lid: any;
  lecTitle: string;
  owned: boolean;
  lecData: any;
  onAction: (params: Object) => void;
  notesZone: React.ReactNode;
  portrait: boolean;
  classes: any;
}

interface TabContainerProps {
  children?: React.ReactNode;
  value: Number;
  index: Number;
}

function TabContainer(props: TabContainerProps) {
  const { index, value, children } = props;
  return (
    <Typography component="div">
      {
        // for 'lazy mount', add value/index checking
        value === index && children
      }
    </Typography>
  );
}

const SidePanelBase: React.FC<Props> = ({
  getCurrentInfoBox,
  currentCourse,
  currentLecture,
  user,
  classes,
  lid,
  lecTitle,
  owned,
  lecData,
  onAction,
  notesZone,
  portrait,
}: Props) => {
  const [tabIdxL, setTabIdxL] = useState(0); // tab index for landscape
  const [tabIdxP, setTabIdxP] = useState(0); // tab index for portrait

  var lecProgress = {}; // cache the progress of each lecture
  var indProgress = {}; // cache the { value, buffer } pair derived from lecProgress (see getProgress)

  const liked = lecData && lecData[lid] && lecData[lid].liked ? true : false;

  const typeLabelClasses: string[] = ["Hdo", "Card", "Test", "Asm", "Link"];

  const typeIcons: React.ReactNode = [
    FileCopy,
    Subtitles,
    PlaylistAddCheck,
    Assignment,
    Link,
  ];

  useEffect(() => {
    // scroll the current lec to top of list in Progress tab
    const lec = document.getElementById(`progLec-${lid}`);
    const prog = document.getElementById("progLec");

    if (prog && lec) {
      const lecH = lec.clientHeight;
      const scrollTopLimit = prog.scrollHeight - prog.clientHeight;

      while (lec.offsetTop < prog.scrollTop) {
        prog.scrollTop -= prog.scrollTop - lec.offsetTop;
      }
      while (lec.offsetTop + lecH > prog.scrollTop + prog.clientHeight) {
        if (prog.scrollTop + lecH > scrollTopLimit) {
          prog.scrollTop = scrollTopLimit;
          break;
        }
        prog.scrollTop += lecH;
      }
    }
  }, [lid]);

  // We may show one of three cases in Clipboard
  // 1. No infobox in this lecture, so "There's no Clipboard Items..."
  // 2. currentLec is loaded, with lid matched, show the Infobox list
  // 3. show the "Show Clipboard Items" Button
  const renderIbxList = () => {
    const { lectures } = currentCourse;
    const idx = lectures.findIndex((lec: any) => lec._id === lid);

    if (lectures[idx].ibxCount === 0) {
      return (
        <div className={classes.ibxList} style={{ fontSize: "1.5rem" }}>
          There is no Clipboard Items for this lecture.
        </div>
      );
    } else if (currentLecture && currentLecture._id === lid) {
      const { sched } = currentLecture;
      return (
        <div className={classes.ibxList}>
          {currentLecture.infoboxes.map((ibx: any, ii: number) => {
            const type: number = Number(ibx.type);
            const TagName = typeIcons[type - 1];
            return (
              <div
                id={`${idIBox}${ii}`}
                key={ibx._id}
                className={classes.infoBox}
                style={{ cursor: sched ? "not-allowed" : "pointer" }}
                onClick={() =>
                  (!sched || isCtMan(user.tc)) && infoBoxHandler(ibx)
                }>
                <div
                  className={classes[`iboxLabel${typeLabelClasses[type - 1]}`]}>
                  <TagName />
                  &nbsp;
                  {ibx.label}
                </div>
                <div className={classes.iboxTitle}>{ibx.title}</div>
              </div>
            );
          })}
        </div>
      );
    } else {
      return clipboardButton(lid);
    }
  };

  const renderClipbaord = () => (
    <div className={classes.clipboard}>{renderIbxList()}</div>
  );

  // retrieve the progress from the own data (lecData)
  // output: { value, buffer } as the parameters of LinearProgress
  // value comes from the number of milestones played x 10 (e.g., played 40%, 30%, 10%; value is 3 X 10)
  // buffer is the farest milestone played x 10 (e.g., played 70%, 20%, 10%; buffer is 7 x 10 )
  const getProgress = (lecId: string) => {
    var progress = (lecData && lecData[lecId] && lecData[lecId].progress) || 0;
    if (lecProgress[lecId] === progress) {
      return indProgress[lecId];
    }
    lecProgress = { ...lecProgress, [lecId]: progress };

    var value = 0;
    var buffer = 0;

    if (progress === 1023) {
      // 2^10, means all 10% milestone are played, considered COMPLETED.
      value = buffer = 100;
    } else if (progress !== 0) {
      while (progress) {
        if (progress % 2) value++; // use "value" for count of '1'
        progress = progress >> 1;
        buffer++; // use "buffer" bit count (either 1 or 0)
      }
      value *= 10;
      buffer *= 10;
    }
    // else (progress === 0): just return { value: 0, buffer 0 }

    indProgress = { ...indProgress, [lecId]: { value, buffer } };
    return { value, buffer };
  };

  const renderLectures = () => {
    return (
      <div className={classes.lecPanel}>
        <div id="progLec" className={classes.lecList}>
          {currentCourse.lectures.map((lec: any, idx: number) => {
            const isCurLec = lec._id === lid;
            const color = lec.sched
              ? "#B0B0B0"
              : isCurLec
              ? "#F9B724"
              : "#FFFFFF";
            const fontWeight = isCurLec ? 600 : 300;
            const { value, buffer } = getProgress(lec._id);

            return (
              <div
                className={classes.lecture}
                id={`progLec-${lec._id}`}
                key={idx}
                style={{
                  color,
                  fontWeight,
                  cursor: lec.sched ? "default" : "pointer",
                }}
                onClick={() =>
                  (lec.sched !== true || isCtMan(user.tc)) &&
                  onAction({ action: "goLec", str: lec._id })
                }>
                <>
                  {idx + 1}. {lec.title}
                  <div className={classes.lecProgress}>
                    <LinearProgress
                      classes={{ determinate: classes.progressBar1 }}
                      style={{ width: "78%", marginRight: "1rem" }}
                      variant={isCurLec ? "buffer" : "determinate"}
                      value={value}
                      valueBuffer={buffer}
                      color="secondary"
                    />
                    {!lec.act && !lec.sched && (
                      <span className={classes.lecLength}>{lec.length}</span>
                    )}
                  </div>
                </>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderNotes = () => (
    <div className={classes.mobileNotes}>{notesZone}</div>
  );

  const tabsData = [
    // label, icon, body (handle routine), and show/hide in landscape mode
    {
      label: "Progress",
      icon: (
        <i className="fas fa-tasks" style={{ width: "2rem", height: "2rem" }} />
      ),
      body: renderLectures,
      id: idTabProg,
      ls: true,
    },
    {
      label: "Clipboard ",
      icon: (
        <i
          className="far fa-clipboard"
          style={{ width: "2.3rem", height: "2.3rem" }}
        />
      ),
      body: renderClipbaord,
      id: idTabClip,
      ls: true,
    },
    {
      label: "Notes",
      icon: (
        <i
          className="far fa-sticky-note"
          style={{ width: "2.2rem", height: "2.2rem" }}
        />
      ),
      body: renderNotes,
      id: idTabNote,
      ls: false,
    },
  ];

  const tabItems = (portrait: boolean) => {
    const tabItems: React.ReactNode[] = [];
    tabsData.forEach((tab: any, idx: number) => {
      if (portrait || tab.ls) {
        tabItems.push(
          <Tab
            key={idx}
            classes={{ root: classes.tabRoot }}
            label={tab.label}
            icon={<span id={tab.id}>{tab.icon}</span>}
          />,
        );
      }
    });
    return tabItems;
  };

  const tabBodies = (portrait: boolean, value: number) => {
    const tabBodies: React.ReactNode[] = [];
    tabsData.forEach((tab: any, idx: number) => {
      if (portrait || tab.ls) {
        tabBodies.push(
          // only put currently viewable tab body in <Tabs />
          <TabContainer key={idx} index={idx} value={value}>
            {idx === value && tab.body()}
          </TabContainer>,
        );
      }
    });
    return tabBodies;
  };

  const renderTabs = (portrait: boolean) => {
    const value = portrait ? tabIdxP : tabIdxL;
    return (
      <div className={classes.sideTabsContent}>
        <Tabs
          classes={{ root: classes.tabsRoot }}
          value={value}
          onChange={(e, newValue) =>
            portrait ? setTabIdxP(newValue) : setTabIdxL(newValue)
          }
          variant="scrollable"
          scrollButtons="on"
          indicatorColor="primary"
          textColor="primary">
          {tabItems(portrait)}
        </Tabs>
        <SwipeableViews
          index={value}
          onChangeIndex={(index: number) => {
            portrait ? setTabIdxP(index) : setTabIdxL(index);
          }}>
          {tabBodies(portrait, value)}
        </SwipeableViews>
      </div>
    );
  };

  const clipboardButton = (lec_id: string) => (
    <div
      id={idShowClip}
      className={classes.clipboardCoverButton}
      onClick={() => onAction({ action: "getLec", str: lec_id })}>
      Show Clipboard Items
    </div>
  );

  const infoBoxHandler = (ibx: any) => {
    const { _id, type } = ibx;
    if (type === infoboxType.LINK) {
      onAction({ action: "link", str: ibx.aux });
    } else {
      if (user && owned) {
        getCurrentInfoBox(_id);
      }
      // Classroom takes care the alert for (!user || !owned)
      onAction({ action: "ibx", str: _id });
    }
  };

  const navButton = (
    hint: string,
    action: string,
    disabled: boolean,
    icon: React.ReactNode,
    id: string,
  ) => (
    <Tooltip title={hint}>
      <span>
        <Button
          id={id}
          classes={{ root: classes.navButton }}
          size="small"
          onClick={() => onAction({ action })}
          style={{ marginRight: action === "back" ? "1rem" : "6px" }}
          disabled={disabled}>
          {icon}
        </Button>
      </span>
    </Tooltip>
  );

  const likeButton = () => (
    <Tooltip title={liked ? "Unmark as Favorite" : "Mark as Favorite"}>
      <Button
        classes={{ root: classes.likeButton }}
        size="medium"
        onClick={() => onAction({ action: "like", bool: !liked })}>
        {liked ? <Favorite /> : <FavoriteBorder />}
      </Button>
    </Tooltip>
  );

  const lecControl = () => (
    <>
      {navButton(
        "Back to Course Page",
        "back",
        false,
        <>
          <ArrowBack />
          <List />
        </>,
        idLecBack,
      )}
      {navButton(
        "Previous Lecture",
        "prev",
        isFirstLecture,
        <SkipPrevious />,
        idLecPrev,
      )}
      {navButton(
        "Next Lecture",
        "next",
        isLastLecture,
        <SkipNext />,
        idLecNext,
      )}
    </>
  );

  const isFirstLecture = lid === currentCourse.lectures[0]._id;
  const isLastLecture =
    lid === currentCourse.lectures[currentCourse.lectures.length - 1]._id;

  return (
    <>
      <div className={classes.headBoard}>
        <div className={classes.headBoardContent}>
          {owned && likeButton()}
          <div className={classes.headBoardTitle}>{lecTitle}</div>
          <div className={classes.headBoardControl}>{lecControl()}</div>
        </div>
      </div>
      <div className={classes.sideTabs}>{renderTabs(portrait)}</div>
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    currentCourse: selectorCurrentCourse(state),
    currentLecture: selectorCurrentLecture(state),
    user: state.state_auth,
  };
}

const base: any = SidePanelBase;
export const SidePanel: any = withStyles(styles)(
  connect(mapStateToProps, { getCurrentInfoBox })(base),
);

export const SidePanelTest = withStyles(styles)(SidePanelBase);
